<script setup lang="ts">
import { computed } from 'vue';
import type { ArrayColumnBadge } from '@/components/arrayColumns';
import { LazyTooltip } from '@/components/ui/tooltip';
import { formatCount } from '@/utils/formatCount';

/**
 * The split schedule counts from the table cell (decision D1): filled for the
 * list's own zone, outlined for subscriber zones, and destructive for a problem
 * count. The counts themselves are labels with no hover of their own. A caller
 * that makes them clickable wraps them in a button styled with COUNT_BUTTON_CLASS from countButton.ts.
 * Zero counts are hidden unless the badge sets alwaysShow or carries `text`.
 *
 * `layout`:
 * - `separate` (default): each count its own badge, a small gap apart.
 * - `joined`: the counts touch and share their inner edge, so they read as one
 *   segmented control (the search card's schedule line). No icons: the order
 *   of the counts already says which is which, and the tooltips say it in words.
 * - `bare`: no border and no fill — just the number and then its icon, in the same text
 *   colour as the card's other counts (the card's track and subscriber zone counts).
 *
 * `columns` (bare only) lays one count into two columns of the parent grid: the
 * number in the first, right-aligned, and the icon in the second. The search
 * card stacks its counts this way, so the numbers line up on their right edge
 * and the icons line up on the card's right edge.
 */
// The root is the lazy tooltip, which draws no box, so a caller's class (ml-auto,
// shrink-0) and test id go on the control itself.
defineOptions({ inheritAttrs: false });

const props = withDefaults(
    defineProps<{ badges: ArrayColumnBadge[]; layout?: 'separate' | 'joined' | 'bare'; columns?: boolean }>(),
    { layout: 'separate', columns: false },
);

const visible = computed(() => props.badges.filter((badge) => badge.count > 0 || badge.alwaysShow || badge.text !== undefined));

/** What the badge shows in place of a number: its `text` when it has one, otherwise the formatted count. */
function shownValue(badge: ArrayColumnBadge): string {
    return badge.text ?? formatCount(badge.count);
}

// `columns` only means something for the bare layout, so every class that reads it reads this.
const columnsMode = computed(() => props.layout === 'bare' && props.columns);

/**
 * One tooltip for the whole control, one line per count. A tooltip per count
 * made the reader hunt for each count's own few pixels and read them one at a
 * time, and the browser's native `title` only shows after the pointer rests.
 */
const tooltipLines = computed(() =>
    visible.value
        .map((badge) => badge.label ?? (badge.title ? `${shownValue(badge)} ${badge.title}` : null))
        .filter((line): line is string => line !== null),
);

// `relative`, because the hidden label below is `sr-only`, which is
// `position: absolute`. A badge that is not positioned leaves the label to the
// nearest ancestor that is. On the search page that was the layout's content
// region, outside the card list's scroll container, which cannot clip it: the
// labels of every card scrolled out of view sat below the viewport, and the
// whole page grew a scrollbar the height of the list.
const BASE = 'relative inline-flex shrink-0 items-center justify-center gap-1 text-xs font-medium';
const VARIANTS: Record<ArrayColumnBadge['variant'], string> = {
    filled: 'text-primary bg-primary/10 border-transparent',
    outlined: 'text-primary border-primary/30',
    problem: 'text-destructive border-destructive/50',
};

function badgeClass(badge: ArrayColumnBadge, index: number): string[] {
    if (columnsMode.value) {
        return ['relative col-span-2 grid grid-cols-subgrid items-center text-xs font-medium text-foreground tabular-nums'];
    }
    if (props.layout === 'bare') return [BASE, 'text-foreground tabular-nums'];
    const shape =
        props.layout === 'joined'
            ? [
                  'px-2 py-0.5 border',
                  index === 0 ? 'rounded-l' : '-ml-px',
                  index === visible.value.length - 1 ? 'rounded-r' : '',
              ].join(' ')
            : 'px-2 py-0.5 border rounded';
    return [BASE, shape, VARIANTS[badge.variant]];
}
</script>

<template>
    <LazyTooltip :enabled="tooltipLines.length > 0" side="top" align="end">
        <span
            class="shrink-0 cursor-default items-center"
            :class="[
                columnsMode ? 'col-span-2 grid grid-cols-subgrid' : 'inline-flex',
                layout === 'separate' ? 'gap-1' : '',
            ]"
            :data-layout="layout"
            data-testid="schedule-badge-pair"
            v-bind="$attrs"
        >
            <span
                v-for="(badge, index) in visible"
                :key="badge.key"
                :class="badgeClass(badge, index)"
                :data-badge="badge.key"
                data-testid="schedule-badge"
            >
                <component
                    :is="badge.icon"
                    v-if="badge.icon && layout !== 'joined'"
                    class="size-3 shrink-0"
                    :class="columnsMode ? 'col-start-2 row-start-1 justify-self-end' : layout === 'bare' ? 'order-last' : ''"
                    aria-hidden="true"
                />
                <!-- A plain span's aria-label is ignored, so the phrase is read from hidden text. -->
                <span
                    :aria-hidden="badge.label || badge.title ? 'true' : undefined"
                    :class="columnsMode ? 'col-start-1 row-start-1 justify-self-end' : ''"
                    data-testid="schedule-badge-count"
                    >{{ shownValue(badge) }}</span
                >
                <span v-if="badge.label || badge.title" class="sr-only" data-testid="schedule-badge-label">{{
                    badge.label ?? `${shownValue(badge)} ${badge.title}`
                }}</span>
            </span>
        </span>
        <template #content>
            <div class="flex flex-col gap-1" data-testid="schedule-badge-tooltip">
                <div v-for="line in tooltipLines" :key="line">{{ line }}</div>
            </div>
        </template>
    </LazyTooltip>
</template>
